import { useState, useEffect } from 'react';
import { Text } from '@qwickapps/react-framework';
import { useAuth } from '@qwickapps/auth-client';
import { config } from '../../config/runtime';
import { useJournalCosts } from './useSSE';
import type { AgentCostData } from './useSSE';

const CLAUDE_BUDGET = 200;
const CODEX_BUDGET = 20;

interface OtelCostData {
  configured: boolean;
  totalCostUsd: number;
  p99LatencyMs: number;
  agentCosts: AgentCost[];
  since: string;
}

interface AgentCost {
  agentId: string;
  agentName: string;
  inputTokens: number;
  outputTokens: number;
  estimatedCost?: number;
}

interface CostOverviewProps {
  costs?: AgentCostData[];
  loading?: boolean;
  monthlyBudget?: number;
}

export default function CostOverview({
  costs: propCosts,
  loading: propLoading,
  monthlyBudget = 220,
}: CostOverviewProps) {
  const { session } = useAuth();
  const { costs: fetchedCosts, loading: fetchLoading } = useJournalCosts();
  const [data, setData] = useState<OtelCostData | null>(null);
  const [otelLoading, setOtelLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const costs = propCosts !== undefined ? propCosts : fetchedCosts;
  const loading = propLoading !== undefined ? propLoading : (fetchLoading || otelLoading);

  useEffect(() => {
    if (!session?.accessToken) {
      setOtelLoading(false);
      setError('No session');
      return;
    }

    fetch(`${config.apiUrl}/api/dashboard/costs`, {
      headers: { Authorization: `Bearer ${session.accessToken}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json() as Promise<OtelCostData>;
      })
      .then(d => {
        setData(d);
        setOtelLoading(false);
      })
      .catch(err => {
        setError(String(err));
        setOtelLoading(false);
      });
  }, [session?.accessToken]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
        <div role="progressbar" aria-label="loading" style={{
          width: 24,
          height: 24,
          border: '2px solid var(--theme-border-color)',
          borderTopColor: 'var(--theme-color-primary)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        style={{
          padding: 24,
          borderRadius: 8,
          background: 'var(--theme-bg-surface)',
          border: '1px solid var(--theme-border-color)',
        }}
      >
        <Text variant="h6" content="Token Usage & Costs" gutterBottom />
        <Text
          variant="body2"
          content="Could not load cost metrics. Check Grafana Cloud configuration."
          customColor="var(--theme-text-secondary)"
        />
      </div>
    );
  }

  const totalTokens = data.agentCosts.reduce(
    (sum, c) => sum + c.inputTokens + c.outputTokens,
    0,
  );

  const claudeCosts = costs.filter(
    c => !['codex', 'opencode'].includes(c.agentId.toLowerCase()),
  );
  const codexCosts = costs.filter(c => c.agentId.toLowerCase() === 'codex');

  const claudeTotal = claudeCosts.reduce((sum, c) => sum + (c.estimatedCost ?? 0), 0);
  const codexTotal = codexCosts.reduce((sum, c) => sum + (c.estimatedCost ?? 0), 0);
  const totalCost = costs.reduce((sum, c) => sum + (c.estimatedCost ?? 0), 0);

  const assumedPricePerMillion = 15;
  const payPerTokenCost = (totalTokens / 1_000_000) * assumedPricePerMillion;
  const savings = monthlyBudget - payPerTokenCost;
  const savingsPercent =
    payPerTokenCost > 0 ? Math.round((savings / payPerTokenCost) * 100) : 0;

  const claudeBurnPct = Math.min(100, Math.round((claudeTotal / CLAUDE_BUDGET) * 100));
  const codexBurnPct = Math.min(100, Math.round((codexTotal / CODEX_BUDGET) * 100));

  return (
    <div
      style={{
        padding: 24,
        borderRadius: 8,
        background: 'var(--theme-bg-surface)',
        border: '1px solid var(--theme-border-color)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <Text variant="h6" content="Token Usage & Costs" />
        {!data.configured && (
          <Text
            variant="caption"
            content="Grafana Cloud not configured — showing zeros"
            customColor="var(--theme-text-disabled)"
          />
        )}
      </div>

      <div style={{ display: 'flex', gap: 32, marginTop: 24, flexWrap: 'wrap' }}>
        <StatBox
          label="24 h Cost"
          value={`$${data.totalCostUsd.toFixed(4)}`}
          color={data.totalCostUsd > monthlyBudget ? '#f44336' : undefined}
        />
        <StatBox label="Total Tokens (24 h)" value={totalTokens.toLocaleString()} />
        <StatBox
          label="p99 Latency"
          value={data.p99LatencyMs > 0 ? `${data.p99LatencyMs.toLocaleString()} ms` : '—'}
        />
        <StatBox label="Monthly Budget" value={`$${monthlyBudget}`} />
        <StatBox
          label="Pay-per-token est."
          value={`$${payPerTokenCost.toFixed(2)}`}
          color={payPerTokenCost > monthlyBudget ? '#f44336' : undefined}
        />
        <StatBox
          label="Savings"
          value={`$${savings.toFixed(2)}`}
          color={savings > 0 ? '#4caf50' : '#f44336'}
        />
      </div>

      {/* Budget Burn Rate */}
      <div style={{ marginTop: 24 }}>
        <Text variant="subtitle2" content="Budget Burn Rate" gutterBottom />
        <BudgetBar
          label="Claude Sonnet"
          spent={claudeTotal}
          budget={CLAUDE_BUDGET}
          pct={claudeBurnPct}
          color="#7c3aed"
        />
        <BudgetBar
          label="Codex"
          spent={codexTotal}
          budget={CODEX_BUDGET}
          pct={codexBurnPct}
          color="#2196f3"
        />
      </div>

      <div style={{ marginTop: 24, padding: 16, background: 'var(--theme-bg-secondary)', borderRadius: 4 }}>
        <Text variant="body2" content="Monthly Billing Advantage" />
        <Text
          variant="caption"
          content={`You save ${savingsPercent}% by using monthly billing instead of pay-per-token pricing`}
          customColor={savings > 0 ? '#4caf50' : 'var(--theme-text-secondary)'}
        />
      </div>

      {costs.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Text variant="subtitle2" content="By Agent (24 h)" gutterBottom />
          {costs.map(cost => (
            <div
              key={cost.agentId}
              style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}
            >
              <Text variant="body2" content={cost.agentName} />
              <div style={{ display: 'flex', gap: 16 }}>
                <Text
                  variant="body2"
                  content={`${(cost.inputTokens + cost.outputTokens).toLocaleString()} tokens`}
                />
                <Text
                  variant="body2"
                  content={`$${(cost.estimatedCost ?? 0).toFixed(4)}`}
                  customColor="var(--theme-text-secondary)"
                />
              </div>
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingTop: 8,
              borderTop: '1px solid var(--theme-border-color)',
              marginTop: 8,
            }}
          >
            <Text variant="body2" content={`Total: $${totalCost.toFixed(2)}`} />
          </div>
        </div>
      )}

      {data.configured && data.agentCosts.length === 0 && costs.length === 0 && (
        <div style={{ marginTop: 24, padding: 16, textAlign: 'center' }}>
          <Text
            variant="body2"
            content="No agent activity in the last 24 h"
            customColor="var(--theme-text-secondary)"
          />
        </div>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

interface StatBoxProps {
  label: string;
  value: string;
  color?: string;
}

function StatBox({ label, value, color }: StatBoxProps) {
  return (
    <div style={{ textAlign: 'center', minWidth: 80 }}>
      <Text
        variant="h5"
        content={value}
        customColor={color || 'var(--theme-text-primary)'}
      />
      <Text
        variant="caption"
        content={label}
        customColor="var(--theme-text-secondary)"
      />
    </div>
  );
}

interface BudgetBarProps {
  label: string;
  spent: number;
  budget: number;
  pct: number;
  color: string;
}

function BudgetBar({ label, spent, budget, pct, color }: BudgetBarProps) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <Text variant="body2" content={label} />
        <Text
          variant="caption"
          content={`$${spent.toFixed(2)} / $${budget}`}
          customColor={pct >= 80 ? '#f44336' : 'var(--theme-text-secondary)'}
        />
      </div>
      <div
        style={{
          height: 8,
          borderRadius: 4,
          background: 'var(--theme-border-color)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: pct >= 80 ? '#f44336' : color,
            borderRadius: 4,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      <Text
        variant="caption"
        content={`${pct}% used`}
        customColor="var(--theme-text-secondary)"
      />
    </div>
  );
}
